import _ from "lodash";
import { allPlays } from "games/allPlays";
import { isDisplayable, convertNum } from "util/misc";

const getLastNthPlay = (n, plays) => {
  return _.get(plays, plays.length - n, {});
};

// play interruptions are outside of the typical game flow
// dont contain yard line / distance to go / down / length of play info
const isPlayInterruption = (playType) => {
  const interruptions = ["timeout", "officialTimeout", "weatherDelay"];
  return interruptions.includes(playType);
};

// @todo update description
// @todo the best approach?
// if previous play is timeout, skip over timeout values so new presets
// will be relative to the game state and not the timeout state
const getPreviousPlay = (plays, playIndex = null) => {
  if (!plays.length || playIndex === 0) return {};
  const previousPlay = playIndex
    ? _.get(plays, playIndex - 1, {})
    : getLastNthPlay(1, plays);

  if (isPlayInterruption(previousPlay.playType)) {
    // previous previous play
    return playIndex
      ? _.get(plays, playIndex - 2, {})
      : getLastNthPlay(2, plays);
  } else {
    return previousPlay;
  }
};

const getIsEndOfGame = (lastPlay) => {
  const isTie = lastPlay.homeTotalPoints === lastPlay.awayTotalPoints;
  return lastPlay.quarter > 3 && lastPlay.playType === "endOfQuarter" && !isTie;
};

const getOppositeSide = (side) => {
  return side === "home" ? "away" : "home";
};

const getTeamFromGame = (game, side) => {
  return _.get(game, [side, "season", "team"]);
};

const getSchoolNameFromGame = (game, side) => {
  return _.get(getTeamFromGame(game, side), "school.name", "");
};

const getSeasonTeamIdBySide = (game, side) => {
  return _.get(game, [side, "season", "id"]);
};

const getTeamSideFromId = (game, seasonTeamId) => {
  if (seasonTeamId === getSeasonTeamIdBySide(game, "home")) {
    return "home";
  } else if (seasonTeamId === getSeasonTeamIdBySide(game, "away")) {
    return "away";
  }
};

const convertNumByFieldName = (val, fieldName) => {
  const numFields = [
    "down",
    "distanceToGo",
    "yardLine",
    "ballSpot",
    "quarter",
    "lengthOfPlay"
  ];
  if (!numFields.includes(fieldName)) return val;
  return convertNum(val);
};

const getFieldDiff = (editedPlay, originalPlay, fieldName) => {
  const oField = _.get(originalPlay, fieldName, 0) || 0;
  const eField = _.get(editedPlay, fieldName, 0) || 0;
  return eField - oField;
};

const getPlayTypeNameById = (id) => {
  return _.get(
    allPlays.find((p) => p.id === id),
    "name",
    ""
  );
};

const getPlayFieldsByPlayTypeId = (id) => {
  return _.get(
    allPlays.find((p) => p.id === id),
    "fields",
    ""
  );
};

// if there is no team code, get the first 2 characters of the school name
// and use that instead
const getTeamCodeFallback = (schoolName) => {
  if (!schoolName) return "";
  const teamCodeFallback = schoolName
    .replace(/[-. ]/g, "")
    .slice(0, 2)
    .toUpperCase();

  return teamCodeFallback;
};

const getTeamCode = (teamSide, game) => {
  const team = getTeamFromGame(game, teamSide);
  const schoolName = _.get(team, "school.name");
  const schoolCode = _.get(team, "teamCode");
  return schoolCode || getTeamCodeFallback(schoolName);
};

const getPlayTypeInfo = (playType) => {
  return allPlays.find((p) => p.id === playType);
};

const fieldExistsOnPlayType = (playType, inputName) => {
  return _.get(getPlayTypeInfo(playType), "fields", []).includes(inputName);
};

// fields that are always required on the play by play screen
const requiredFields = [
  "ballSpotTeamId",
  "ballSpot",
  "teamInPossessionId",
  "playType",
  "player1Id",
  "player2Id"
];

const getInputError = (fieldName, value) => {
  if (requiredFields.includes(fieldName)) {
    const isError = !isDisplayable(value);
    return {
      isError: isError,
      message: isError ? "required" : ""
    };
  }

  return {
    isError: false,
    message: ""
  };
};

const getPlayTypeSuggestions = (previousPlay) => {
  const { playType = null, includesTouchdown = false } = previousPlay;

  const map = {
    safety: ["freeKickReturn", "fairCatch", "touchback"],
    punt: [
      "puntReturn",
      "muffedPunt",
      "blockedPuntRecoveryInEndZone",
      "blockedPuntReturn"
    ],
    interception: ["interceptionReturn"],
    PATPass: [
      "kickoffReturn",
      "PATInterceptionReturn",
      "PATFumbleReturn",
      "PATBlockedKickReturn",
      "fairCatch",
      "touchback"
    ],
    PATRun: [
      "kickoffReturn",
      "PATInterceptionReturn",
      "PATFumbleReturn",
      "PATBlockedKickReturn",
      "fairCatch",
      "touchback"
    ],
    PATKick: [
      "kickoffReturn",
      "PATInterceptionReturn",
      "PATFumbleReturn",
      "PATBlockedKickReturn",
      "fairCatch",
      "touchback"
    ],
    fieldGoal: ["kickoffReturn", "fairCatch", "touchback"]
  };

  const suggestionsBasedOnPreviousPlayType = _.get(map, playType);
  if (suggestionsBasedOnPreviousPlayType) {
    return allPlays.filter((play) =>
      suggestionsBasedOnPreviousPlayType.includes(play.id)
    );
  } else if (includesTouchdown) {
    const playTypes = ["PATPass", "PATKick", "PATRun"];
    return allPlays.filter((play) => playTypes.includes(play.id));
  }

  return null;
};

// the returned graphql data fields do not have dashes
// this converts the sport code value (with dashes) to the returned data field value (no dashes)
// to help make graphql queries and other functions data driven
const getDataSportCode = (sportCode) => {
  const map = {
    "girls-volleyball": "girlsvolleyball",
    "girls-basketball": "basketball",
    "boys-basketball": "basketball",
    "girls-soccer": "soccer",
    "boys-soccer": "soccer",
    softball: "baseball",
    baseball: "baseball",
    football: "football"
  };
  if (!sportCode) {
    console.warn(`getDataSportCode - does not support sportCode ${sportCode}`);
  }
  return _.get(map, sportCode);
};

const transformGameStatusIfDateAdded = (gameStatus, gameDateString) => {
  const justAddedDate =
    (!gameStatus || gameStatus === "PRELIMINARY") && gameDateString;
  return justAddedDate ? "SCHEDULED" : gameStatus;
};

/*
 * @param {object} stats
 * @param {string[]} allowedKeys - array of allowed keys
 * @example
 *    filterStatsByAllowedKeys({totalPoints: 10, totalYards: 20}, ["totalPoints"])
 */
const filterStatsByAllowedKeys = (stats, allowedKeys) =>
  Object.keys(stats, allowedKeys)
    .filter((key) => allowedKeys.includes(key))
    .reduce((obj, key) => {
      return {
        ...obj,
        [key]: stats[key]
      };
    }, {});

/*
 * @param {object} stats
 * @param {string[]} blocked keys - array of NOT allowed / blocked keys
 * @example
 *  filterStatsByBlockedKeys({totalPoints: 10, totalYards: 20}, ["totalPoints"])
 */
const filterStatsByBlockedKeys = (stats, blockedKeys) =>
  Object.keys(stats, blockedKeys)
    .filter((key) => !blockedKeys.includes(key))
    .reduce((obj, key) => {
      return {
        ...obj,
        [key]: stats[key]
      };
    }, {});

export {
  getFieldDiff,
  getPreviousPlay,
  getSeasonTeamIdBySide,
  getTeamFromGame,
  getSchoolNameFromGame,
  getOppositeSide,
  getTeamSideFromId,
  convertNumByFieldName,
  isPlayInterruption,
  getPlayTypeNameById,
  getTeamCodeFallback,
  getTeamCode,
  getPlayFieldsByPlayTypeId,
  getPlayTypeInfo,
  fieldExistsOnPlayType,
  getInputError,
  requiredFields,
  getPlayTypeSuggestions,
  getIsEndOfGame,
  getDataSportCode,
  transformGameStatusIfDateAdded,
  filterStatsByAllowedKeys,
  filterStatsByBlockedKeys
};
